import { lazy, Suspense, useEffect } from 'react'
import { RouteSkeleton } from './ds/states/RouteSkeleton'
import type { ReactNode } from 'react'
import { Navigate, Routes, Route, useLocation, useParams } from 'react-router'
import RequireAuth from './auth/RequireAuth'
import { useAuth } from './auth/AuthContext'
import RequireApproved from './auth/RequireApproved'
import Login from './routes/Login'
import Register from './routes/Register'
import Forgot from './routes/Forgot'
import ResetPassword from './routes/ResetPassword'
import Landing from './routes/Landing'
import Pricing from './routes/Pricing'
import Faq from './routes/Faq'
import Regulamin from './routes/Regulamin'
import Polityka from './routes/Polityka'
import Zwroty from './routes/Zwroty'
import Zgloszenie from './routes/Zgloszenie'
import { detectPublicLocale, isLocale, publicPath } from './lib/publicPaths'
import { useI18n } from './lib/i18n'
import { IS_NATIVE } from './lib/native'

const Layout = lazy(() => import('./components/Layout'))

/** FAZA 4 paczka 1 (2026-09-05): /batches, /batches/:id and /generations became
 *  /edits and /edits/:id; paczka 12 (2026-09-07): /campaigns and /campaigns/:id became
 *  /autopilot and /autopilot/:id. The REAL redirect is HTTP 301 at the edge
 *  (deploy/Caddyfile) and in Flask (app/legacy_redirects.py); this element covers the
 *  one case a server never sees — an in-app history entry the SPA router resolves itself.
 *  Keeps the id, the query string and the hash. */
function LegacyRedirect({ to }: { to: string }) {
  const { id } = useParams()
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${id ? `/${id}` : ''}${search}${hash}`} replace />
}
const Onboarding = lazy(() => import('./routes/Onboarding'))
const EditDetail = lazy(() => import('./routes/EditDetail'))
const Generator = lazy(() => import('./routes/Generator'))
const LyricsEditor = lazy(() => import('./routes/LyricsEditor'))
const LyricsAdvanced = lazy(() => import('./routes/LyricsAdvanced'))
const Music = lazy(() => import('./routes/Music'))
const ClipPacks = lazy(() => import('./routes/ClipPacks'))
const PackDetail = lazy(() => import('./routes/PackDetail'))
const PackQa = lazy(() => import('./routes/PackQa'))
const Billing = lazy(() => import('./routes/Billing'))
const Order = lazy(() => import('./routes/Order'))
const Settings = lazy(() => import('./routes/Settings'))
const Notifications = lazy(() => import('./routes/Notifications'))
const NotFound = lazy(() => import('./routes/NotFound'))
const Batches = lazy(() => import('./routes/Batches'))
const BatchDetail = lazy(() => import('./routes/BatchDetail'))
const Campaigns = lazy(() => import('./routes/Campaigns'))
const CampaignDetail = lazy(() => import('./routes/CampaignDetail'))
const Accounts = lazy(() => import('./routes/Accounts'))
const Calendar = lazy(() => import('./routes/Calendar'))
const Stats = lazy(() => import('./routes/Stats'))
const Hooks = lazy(() => import('./routes/Hooks'))
const A25Preview = lazy(() => import('./routes/A25Preview'))
const Captions = lazy(() => import('./routes/Captions'))

/**
 * Pierwsze kroki przed pracą. Strażnik siedział w Pulpicie; Pulpit zniknął, więc stoi
 * na trasie startowej (`/edits`) — a `/dashboard` prowadzi na nią przekierowaniem, więc
 * stare wejście działa tak samo. Sam `/onboarding` jest poza strażnikiem, inaczej
 * zapętliłby się na sobie.
 *
 * PACZKA N2 (N0): ten strażnik JEST pętlą, gdy zapis `onboarded` się nie uda — bo
 * `/onboarding` stoi poza `Layout` i nie ma na nim żadnego innego odnośnika. Warunku
 * nie luzujemy (konto bez `onboarded_at` naprawdę nie przeszło Pierwszych kroków);
 * pętli nie ma, bo `Onboarding.finish()` NIE nawiguje po nieudanym PATCH-u. Kto
 * przywróci tam nawigację „mimo wszystko”, przywróci razem z nią zamknięte konto.
 */
function FirstRunGate({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (user && user.onboarded_at == null) return <Navigate to="/onboarding" replace />
  return <>{children}</>
}

function PublicRedirect({ target = '' }: { target?: string }) {
  return <Navigate to={publicPath(detectPublicLocale(), target)} replace />
}

function LocaleRoute({ children }: { children: ReactNode }) {
  const params = useParams()
  const routeLocale = params.locale
  const { locale, setRouteLocale } = useI18n()

  useEffect(() => {
    if (isLocale(routeLocale) && locale !== routeLocale) setRouteLocale(routeLocale)
  }, [locale, routeLocale, setRouteLocale])

  // A first segment that is not a locale is a MISTYPED ADDRESS, so it gets the 404.
  //
  // It used to `<Navigate to={publicPath('pl')} replace />`, and the cost was measured by
  // hashing the frames rather than by reading this line (WALK-DEFECTS D2):
  //
  //   notfound-en-1440  ==  landing-PL-1440   byte for byte   <- not landing-en
  //
  // Every unknown single-segment path fell into `/:locale` and was sent to the Polish
  // marketing page, and arriving there ran `setRouteLocale('pl')`, so an English visitor
  // had their interface switched to Polish as well. The app's `<Route path="*">` could
  // never fire for these addresses: this route had already matched. GATE 3 family — no
  // non-PL customer should meet Polish they did not ask for.
  //
  // Wrapped in `LazyApp` because `NotFound` is a lazy import and these public routes have
  // no Suspense boundary of their own — the authed group carries the only other one. tsc
  // is blind to that; the test below renders it, which is what actually proves it.
  if (!isLocale(routeLocale)) return <LazyApp><NotFound /></LazyApp>
  return <>{children}</>
}

/** Jeden kontrakt ładowania (sesja 08, G2-auth-onboarding-07): `RouteSkeleton` —
 *  z ramą powłoki, gdy to sama powłoka jeszcze się ładuje (`shell`), i sam szkielet
 *  treści dla publicznego 404. Leniwe trasy WEWNĄTRZ powłoki mają własny `Suspense`
 *  w `Layout`, żeby sidebar i dolny pasek stały, gdy treść się wczytuje. */
function LazyApp({ children, shell = false }: { children: ReactNode; shell?: boolean }) {
  return (
    <Suspense fallback={<RouteSkeleton shell={shell} />}>
      {children}
    </Suspense>
  )
}

export default function App() {
  return (
    <>
    <Routes>
      {/* ── PUBLIC ── */}
      {/* Native app: no marketing site — root goes straight to the app (RequireAuth
          bounces to /login without a session). Legal pages stay reachable (store
          mandate); pricing/landing/FAQ stay web-only (consumption-only storefront). */}
      <Route path="/" element={IS_NATIVE ? <Navigate to="/edits" replace /> : <PublicRedirect />} />
      {/* DEV ONLY. A25 review is "screenshot vs artboard" (Domi, DESIGN-RAW-UI), and the
          consent screen is unreachable without campaigns enabled, a batch, a draft
          campaign and connected accounts. This bench renders it from fixtures so the
          reviewer can look at pixels instead of seeding a tenant. `import.meta.env.DEV`
          is false in `vite build`, so it does not exist in a production bundle — and a
          test asserts that, because this route sits outside RequireAuth. */}
      {import.meta.env.DEV && <Route path="/__a25" element={<A25Preview />} />}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      {/* Password recovery lives INSIDE the SPA. /reset/:token is the address the
          emailed link resolves to, so the token never touches a server-rendered
          page with a different layout and an untranslated flash. */}
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      {!IS_NATIVE && <>
        <Route path="/pricing" element={<PublicRedirect target="/pricing" />} />
        <Route path="/faq" element={<PublicRedirect target="/faq" />} />
        <Route path="/:locale" element={<LocaleRoute><Landing /></LocaleRoute>} />
        <Route path="/:locale/pricing" element={<LocaleRoute><Pricing /></LocaleRoute>} />
        <Route path="/:locale/faq" element={<LocaleRoute><Faq /></LocaleRoute>} />
      </>}
      <Route path="/regulamin" element={<PublicRedirect target="/regulamin" />} />
      <Route path="/polityka" element={<PublicRedirect target="/polityka" />} />
      <Route path="/:locale/regulamin" element={<LocaleRoute><Regulamin /></LocaleRoute>} />
      <Route path="/:locale/polityka" element={<LocaleRoute><Polityka /></LocaleRoute>} />
      {/* Zwroty i anulowanie — osobna, linkowalna strona (wymóg Stripe), tab obok Regulaminu. */}
      <Route path="/zwroty" element={<PublicRedirect target="/zwroty" />} />
      <Route path="/:locale/zwroty" element={<LocaleRoute><Zwroty /></LocaleRoute>} />
      {/* DSA art. 16 notice-and-action. Public and anonymous by law, and NOT wrapped
          in !IS_NATIVE: the store build hides the marketing site, but a report
          mechanism the app links to must resolve inside the app too. */}
      <Route path="/zgloszenie" element={<PublicRedirect target="/zgloszenie" />} />
      <Route path="/:locale/zgloszenie" element={<LocaleRoute><Zgloszenie /></LocaleRoute>} />

      {/* ── AUTHENTICATED APP ── */}

      {/* Approval gate: non-approved tenants get the bare PendingPanel, never the app. */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireApproved />}>
        {/* First run. Reached from the first-run gate on `/edits`; the completion flag
            lives on the account (0034), not in this browser, so it does not reappear on
            another device.

            OUTSIDE `Layout`, od paczki 37 (artboardy 07a–09d): Pierwsze kroki to EKRAN
            SKUPIONY — własny pasek górny z trzema segmentami kroku, saldem i jednym
            linkiem wyjścia, i nic poza nim. Powłoka aplikacji obok kreatora dawała dwie
            nawigacje naraz i dwa salda, a krok przestawał się mieścić w 900 px. */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<LazyApp shell><Layout /></LazyApp>}>
          {/* Pulpit zniknął (MINIMAL-2026-09-18, decyzja 2 planu): ekranem startowym
              są Edity. `/dashboard` zostaje jako przekierowanie — stare linki, maile
              i skrót zapisany na ekranie telefonu prowadzą dalej tam, gdzie praca. */}
          <Route path="/dashboard" element={<Navigate to="/edits" replace />} />
          <Route path="/generator" element={<Generator />} />
          <Route path="/edits" element={<FirstRunGate><Batches /></FirstRunGate>} />
          <Route path="/edits/:id" element={<BatchDetail />} />
          <Route path="/generations" element={<LegacyRedirect to="/edits" />} />
          <Route path="/batches" element={<LegacyRedirect to="/edits" />} />
          <Route path="/batches/:id" element={<LegacyRedirect to="/edits" />} />
          <Route path="/library" element={<Navigate to="/edits?mode=all" replace />} />
          <Route path="/edit/:id" element={<EditDetail />} />
          <Route path="/music" element={<Music />} />
          <Route path="/music/:id/lyrics" element={<LyricsEditor />} />
          <Route path="/music/:trackId/lyrics/advanced" element={<LyricsAdvanced />} />
          <Route path="/clip-packs" element={<ClipPacks />} />
          <Route path="/clip-packs/:id" element={<PackDetail />} />
          <Route path="/clip-packs/:id/qa" element={<PackQa />} />
          <Route path="/hooks" element={<Hooks />} />
          {/* Hooków się nie generuje (właściciel 21.09): stary adres prowadzi na Hooki. */}
          <Route path="/hooks/generator" element={<Navigate to="/hooks" replace />} />
          <Route path="/captions" element={<Captions />} />
          <Route path="/autopilot" element={<Campaigns />} />
          <Route path="/autopilot/:id" element={<CampaignDetail />} />
          {/* Sesja 22: Kalendarz siedzi w tej samej grupie Token 2 co `/autopilot` i ma ten
              sam stan „niedostępne”, gdy `GET /api/campaigns` mówi `enabled: false` —
              biały ekran za pozycją nawigacji byłby najgorszym z możliwych wyjaśnień. */}
          <Route path="/kalendarz" element={<Calendar />} />
          {/* Sesja 36: piąta zakładka Publikacji. Trasa istnieje zawsze, ale ZAKŁADKA
              pokazuje się dopiero, gdy API statystyk odpowiada — na instalacji bez tej
              funkcji nikt tu nie trafi z paska, a wpisany adres dostaje ten sam panel
              „czeka”, co reszta grupy Token 2. */}
          <Route path="/statystyki" element={<Stats />} />
          <Route path="/campaigns" element={<LegacyRedirect to="/autopilot" />} />
          <Route path="/campaigns/:id" element={<LegacyRedirect to="/autopilot" />} />
          <Route path="/accounts" element={<Accounts />} />
          {!IS_NATIVE && <Route path="/billing" element={<Billing />} />}
          {/* The order step: what you are buying + the TWO consent statements,
              given BEFORE a Checkout Session exists. Native builds are excluded
              like /billing — iOS top-ups go through IAP, not this flow.

              NOT under /billing/: Caddy sends the whole /billing/* prefix to Flask
              and explicitly excludes it from the SPA (deploy/Caddyfile), because
              that prefix is where the Stripe webhook and the write routes live. A
              client route there 404s in production — which is exactly what it did
              in dev until a screenshot run caught it. */}
          {!IS_NATIVE && <Route path="/order" element={<Order />} />}
          <Route path="/settings" element={<Settings />} />
          {/* 45-B / 46-A (PW): lista zdarzeń konta, dzwonek w powłoce prowadzi tutaj. */}
          <Route path="/powiadomienia" element={<Notifications />} />
          {/* Inside RequireAuth, so this catch-all is only ever reached WITH a session. */}
          <Route path="*" element={<NotFound signedIn />} />
        </Route>
        </Route>
      </Route>
    </Routes>
    {/* NO COOKIE BANNER. The product sets exactly two cookies — the login session
        and the CSRF token — and neither needs consent; there is no analytics
        anywhere in the tree. A banner asking permission for nothing is not
        neutral: it trains people to dismiss the one that will matter, and it
        pointed at a policy that declared analytics we do not run.
        The component, `--cookie-bar-h`, the bottom-edge gate and the harness's
        `?cookie=1` all STAY in the repo on purpose — the day analytics arrive,
        the banner comes back together with a FRESH consent, which the recorded
        scope:['necessary'] already forces. Removing the infrastructure would
        make that day a rebuild instead of a remount. */}
    </>
  )
}
